import { FC, ReactElement, ReactNode, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { PersonalData } from '../PersonalDataContainer/PersonalData';
import { fetchUserThunk } from '../../thunks';
import { Language } from '../LanguageContainer/Language';
import { selectTab, selectTabIndex } from '../../appSlice';
import { Accounts } from '../AccountsContainer/Accounts';
import { localeToLang } from '../../../common/localeHelper';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';
import { LegalInfo } from '../LegalInfoContainer/LegalInfo';
import { getLocale } from '../../../../configuration/lang/langSlice';
import { getIdToken } from '../../../../configuration/tokenHandling/tokenHandlingSlice';

// TODO Tab and Tabs are actually from react-bootstrap
interface Tab {
    headline: ReactElement;
    content: ReactElement;
}

interface BodyProps {
    tabs: Array<Tab>;
    setTabIndex: (n: number) => void;
    tabIndex: number;
}

interface FooterProps {
    onClose: any;
}

const UserSettingsBody = (props: BodyProps) => {
    const { tabs, setTabIndex, tabIndex } = props;

    const renderTabLink = (tab: Tab, index: number, setTabIndex: (n: number) => void, tabIndex: number) => {
        const activeClass = index === tabIndex ? 'active' : '';

        return (
            <li key={`link${index}`} className={activeClass}>
                <a onClick={() => setTabIndex(index)} href={'/#'}>
                    {tab.headline}
                </a>
            </li>
        );
    };

    const renderTabLinks = (tabs: Array<Tab>, setTabIndex: any, tabIndex: any) => {
        return (
            <ul className={'nav nav-pills nav-stacked'}>
                {tabs.map((tab: any, index: any) => renderTabLink(tab, index, setTabIndex, tabIndex))}
            </ul>
        );
    };

    return (
        <div className={'UserSettingsBody container-fluid row padding-0'}>
            <div className={'row'}>
                <div className={'col-xs-4 navigation'}>{renderTabLinks(tabs, setTabIndex, tabIndex)}</div>
                <div className={'col-xs-8 content'}>{tabs[tabIndex].content}</div>
            </div>
        </div>
    );
};

UserSettingsBody.propTypes = {
    tabIndex: PropTypes.number,
    setTabIndex: PropTypes.func.isRequired,
    tabs: PropTypes.arrayOf(
        PropTypes.shape({
            headline: PropTypes.node,
            content: PropTypes.node,
        })
    ),
};

const UserSettingsFooter = (props: FooterProps) => {
    const { onClose } = props;

    return (
        <div className={'btn-toolbar pull-right'}>
            <div className={'btn-group'}>
                <div className={'btn btn-primary'} onClick={onClose}>
                    <FormattedMessage id={'intl-msg:settingsDialog.button.close.label'} />
                </div>
            </div>
        </div>
    );
};

UserSettingsFooter.propTypes = {
    onClose: PropTypes.func,
};

export const UserSettingsDialog: FC = () => {
    const dispatch = useAppDispatch();
    const userId = useAppSelector((state) => {
        const sub = getIdToken(state)?.sub;
        return sub?.split(':')[1];
    });
    const tabIndex = useAppSelector(selectTabIndex);
    const locale = useAppSelector(getLocale);

    const setTabIndex = (tabNumber: number) => {
        dispatch(selectTab(tabNumber));
    };

    useEffect(() => {
        userId && dispatch(fetchUserThunk(userId));
    }, [dispatch, userId]);

    const getTabItem = (headlineKey: string, content: ReactNode) => ({
        headline: <FormattedMessage id={headlineKey} />,
        content: (
            <div>
                <h4 className={'text-color-dark'}>
                    <FormattedMessage id={headlineKey} />
                </h4>
                {content}
            </div>
        ),
    });

    const myTabs: Array<Tab> = [
        getTabItem('intl-msg:settingsDialog.tab.personalData.headline', <PersonalData />),
        getTabItem('intl-msg:settingsDialog.tab.language.headline', <Language />),
        getTabItem('intl-msg:settingsDialog.tab.accounts.headline', <Accounts />),
        getTabItem('intl-msg:settingsDialog.tab.cookies.headline', <Cookies locale={locale} />),
        getTabItem('intl-msg:settingsDialog.tab.legalInfo.headline', <LegalInfo />),
    ];

    return <UserSettingsBody tabIndex={tabIndex} tabs={myTabs} setTabIndex={setTabIndex} />;
};

const Cookies = ({ locale }: { locale: string }) => {
    const lang = localeToLang(locale);
    return (
        <div>
            <FormattedMessage id={'intl-msg:settingsDialog.tab.cookies.text'} />
            <span> </span>
            <a
                href={`https://rio.cloud/${lang}/data-protection/cookies#c3322`}
                target={'_blank'}
                rel={'noopener noreferrer'}
            >
                <FormattedMessage id={'intl-msg:settingsDialog.tab.cookies.click.prompt'} />
            </a>
            .
        </div>
    );
};
